import { useEffect, useContext } from "react";
import { useFormik, FormikProps } from "formik";
import * as Yup from "yup";
import isEqual from "lodash/isEqual";
import { cpf as validateCPF } from "cpf-cnpj-validator";
import { useNavigate } from "react-router-dom";

import { Template, HeaderPage, Button, Summary } from "components";

import Service from "services";
import Locale from "locale";
import { MaskHelper, ErrorHelper, CnpjValidator } from "helpers";
import notify from "components/Toast";
import { FieldStrings } from "locale/LocaleStrings";
import {
  getMaxErrorValidation,
  getMinErrorValidation,
  getZipCodeErrorValidation
} from "utils/yup";
import LocalStorageService from "services/localstorage";
import NewMaterialContext, {
  initialContextValues
} from "../NewMaterialContext";

import {
  Address,
  Billing,
  FormPayment,
  FinancialResponsible
} from "./Partials";
import { PaymentProps } from "./type";

const Payment = (): JSX.Element => {
  const navigate = useNavigate();
  const contextData = useContext(NewMaterialContext);

  const initialValues: PaymentProps = {
    analysis: contextData.analysis,
    billing: contextData.billing,
    summary: contextData.summary,
    delivery: contextData.delivery,
    financialResponsible: contextData.financialResponsible
  };

  const PaymentSchema = Yup.object().shape({
    billing: Yup.object().shape({
      formPayment: Yup.string().required(Locale.errors.chooseOne),
      cardNumber: Yup.string().when("formPayment", {
        is: "credit_card",
        then: Yup.string()
          .required(Locale.errors.required)
          .min(...getMinErrorValidation(13))
          .max(...getMaxErrorValidation(19))
      }),
      cardName: Yup.string().when("formPayment", {
        is: "credit_card",
        then: Yup.string().required(Locale.errors.required)
      }),
      cardCVC: Yup.string().when("formPayment", {
        is: "credit_card",
        then: Yup.string()
          .required(Locale.errors.required)
          .min(...getMinErrorValidation(3))
          .max(...getMaxErrorValidation(4))
      }),
      cardExpiring: Yup.string().when("formPayment", {
        is: "credit_card",
        then: Yup.string()
          .required(Locale.errors.required)
          .test("cardExpiring", Locale.errors.billing.cardExpiring, value => {
            if (!value) return false;
            const [month, year] = value.split("/");

            const expiryDate = new Date(
              2000 + parseInt(year, 10),
              parseInt(month, 10)
            );

            const currentDate = new Date();

            return expiryDate > currentDate;
          })
      }),
      // VALIDACAO CARTAO DE CREDITO
      billingType: Yup.string().required(Locale.errors.chooseOne),
      // VALIDACAO FATURAMENTO
      document: Yup.string()
        .when("billingType", {
          is: "pf",
          then: Yup.string()
            .required(Locale.errors.required)
            .test("is-cpf", "CPF inválido", (value: string | undefined) =>
              validateCPF.isValid(value || "")
            )
        })
        .when("billingType", {
          is: "pj",
          then: Yup.string()
            .required(Locale.errors.required)
            .test("is-cnpj", "CNPJ inválido", (value: string | undefined) =>
              CnpjValidator.isValid(value || "")
            )
        }),
      name: Yup.string().required(Locale.errors.required),
      email: Yup.string().email().required(Locale.errors.required),
      ruralProducerSubscription: Yup.string(),
      stateSubscription: Yup.string(),
      phoneNumber: Yup.string()
        .required(Locale.errors.required)
        .test("11-digits", "Quantidade de dígitos inválida", value =>
          value ? MaskHelper.removeMask(value).length === 11 : false
        ),
      // VALIDACAO ENDERECO DE COBRANCA
      address: Yup.string()
        .required(Locale.errors.required)
        .max(...getMaxErrorValidation(255)),
      number: Yup.string()
        .required(Locale.errors.required)
        .max(...getMaxErrorValidation(20)),
      complement: Yup.string().max(...getMaxErrorValidation(30)),
      neighborhood: Yup.string()
        .required(Locale.errors.required)
        .max(...getMaxErrorValidation(30)),
      zipcode: Yup.string()
        .required(Locale.errors.required)
        .length(...getZipCodeErrorValidation()),
      city: Yup.string()
        .required(Locale.errors.required)
        .max(...getMaxErrorValidation(30)),
      state: Yup.string()
        .required(Locale.errors.required)
        .max(...getMaxErrorValidation(2))
    }),
    // VALIDACAO RESPONSAVEL FINANCEIRO
    financialResponsible: Yup.object().shape({
      name: Yup.string()
        .required(Locale.errors.required)
        .max(...getMaxErrorValidation(255)),
      email: Yup.string()
        .email(Locale.errors.email)
        .required(Locale.errors.required)
        .max(...getMaxErrorValidation(255)),
      mobilePhone: Yup.string()
        .required(Locale.errors.required)
        .test("11-digits", "Quantidade de dígitos inválida", value =>
          value ? MaskHelper.removeMask(value).length === 11 : false
        )
    })
  });

  const clearMaterialSolicitationContextData = () => {
    contextData.analysis = initialContextValues.analysis;
    contextData.delivery = initialContextValues.delivery;
    contextData.billing = initialContextValues.billing;
    contextData.summary = initialContextValues.summary;
    LocalStorageService.clearMaterialFormData();
  };

  const getDocumentType = (type: string) => {
    if (type === "pf") return "CPF";
    if (type === "pj") return "CNPJ";
    return null;
  };

  const getCustomerType = (type: string) => {
    if (type === "pf") return "individual";
    if (type === "pj") return "company";
    return null;
  };

  const getMethodPayment = (method: string) => {
    switch (method) {
      case "boleto":
        return 2;
      case "pix":
        return 3;
      default:
        return 1;
    }
  };

  const formatPhone = (phone: string) => {
    const value = phone?.replace(/\D/g, "");
    return `+55${value}`;
  };

  const phoneParts = (phone: string) => {
    const value = phone?.replace(/\D/g, "");
    return [value.substring(0, 2), value.substring(2)];
  };

  const formatCEP = (cep: string) => cep?.replace(/\D/g, "");

  const getItems = (items: any) =>
    items?.map((item: any, key: number) => ({
      amount: 100 * item.value,
      description: item.label,
      quantity: 1,
      code: String(key)
    }));

  const sendForm = (contextData: any) => {
    const data = {
      address: contextData.delivery.zipcode
        ? {
            postcode: contextData.delivery.zipcode.replace(/\D/g, ""),
            street_name: !contextData.delivery.usePostalBox
              ? contextData.delivery.address
              : undefined,
            number: !contextData.delivery.usePostalBox
              ? contextData.delivery.number
              : undefined,
            complement: !contextData.delivery.usePostalBox
              ? contextData.delivery.complement
              : undefined,
            neighborhood: !contextData.delivery.usePostalBox
              ? contextData.delivery.neighborhood
              : undefined,
            city: contextData.delivery.city,
            state: contextData.delivery.state,
            mailbox: contextData.delivery.postalBox
          }
        : undefined,
      billing_address:
        formik.values.billing.useDeliveryAsBillingAddress &&
        !formik.values.delivery.usePostalBox
          ? undefined
          : {
              postcode: contextData.billing.zipcode.replace(/\D/g, ""),
              street_name: contextData.billing.address,
              number: contextData.billing.number,
              complement: contextData.billing.complement,
              neighborhood: contextData.billing.neighborhood,
              city: contextData.billing.city,
              state: contextData.billing.state
            },
      billing_data: {
        name: contextData.billing.name,
        cpf:
          contextData.billing.billingType === "pf"
            ? contextData.billing.document.replace(/\D/g, "")
            : undefined,
        cnpj:
          contextData.billing.billingType === "pj"
            ? CnpjValidator.sanitize(contextData.billing.document)
            : undefined,
        email: contextData.billing.email,
        state_subscription: contextData.billing.stateSubscription,
        rural_producer_subscription:
          contextData.billing.ruralProducerSubscription
      },
      payment: {
        amount: contextData.summary.amount,
        payment_method: getMethodPayment(contextData.billing.formPayment),
        financial_responsible: {
          name: contextData.financialResponsible.name,
          email: contextData.financialResponsible.email,
          mobile: formatPhone(contextData.financialResponsible.mobilePhone)
        }
      },
      analysis_items: getAnalyses(contextData.analysis.analyses),
      lactation_animals_amount: Number(contextData.analysis.animalCount),
      tank_amount: Number(contextData.analysis.tankCount),
      shipping_method: contextData.delivery.shippingMethod,
      association: contextData.analysis.association.value,
      support_point: contextData.delivery.supportLocationName
    };
    Service.registerMaterial(data)
      .then((responseData: any) => {
        sendTransaction(contextData, responseData.material_order_id);
      })
      .catch(err => {
        ErrorHelper.notifyError(err);
        formik.setSubmitting(false);
      });
  };

  const sendTransaction = (data: PaymentProps, materialOrderId: number) => {
    const billingAddressParts = [data.billing.number, data.billing.address];
    if (data.billing.neighborhood) {
      billingAddressParts.push(data.billing.neighborhood);
    }
    const shippingAddressParts = [data.delivery.number, data.delivery.address];

    const billingPhoneParts = phoneParts(data.billing.phoneNumber);
    const billingPhoneArea = billingPhoneParts[0];
    const billingPhoneNumber = billingPhoneParts[1];
    const billingAddress = {
      country: data.billing.countryCode.toUpperCase(),
      state: data.billing.state,
      city: data.billing.city,
      zip_code: formatCEP(data.billing.zipcode),
      line_1: billingAddressParts.join(", "),
      line_2: data.billing.complement
    };

    let shippingAddress;
    // Clinicalog
    if (data.delivery.shippingMethod === "1") {
      shippingAddress = billingAddress;
    } else {
      shippingAddress = {
        country: "BR",
        state: data.delivery.state,
        city: data.delivery.city,
        zip_code: formatCEP(data.delivery.zipcode),
        line_1: shippingAddressParts.join(", "),
        line_2: data.delivery.complement
      };
    }

    const checkoutData = {
      code: materialOrderId,
      customer: {
        name: data.billing.name,
        type: getCustomerType(data.billing.billingType),
        email: data.billing.email,
        code: data.billing.document,
        document:
          data.billing.billingType === "pj"
            ? CnpjValidator.sanitize(data.billing.document)
            : data.billing.document.replace(/\D/g, ""),
        document_type: getDocumentType(data.billing.billingType),
        address: billingAddress,
        phones: {
          home_phone: {
            country_code: "55",
            area_code: billingPhoneArea,
            number: billingPhoneNumber
          }
        }
      },
      items: getItems(data.summary.items),
      shipping: {
        amount: data.summary.shipping * 100,
        description: data.billing.name,
        recipient_name: data.billing.name,
        recipient_phone: data.billing.phoneNumber,
        address: shippingAddress
      },
      payments: []
    };
    if (data.billing.formPayment === "boleto") {
      (checkoutData as any).payments = [
        {
          payment_method: "boleto",
          boleto: {
            bank: "341",
            instructions:
              "Sr. Caixa, aceitar o pagamento e não cobrar juros após o vencimento."
          }
        }
      ];
    } else {
      const expirationParts = data.billing.cardExpiring.split("/");

      (checkoutData as any).payments = [
        {
          payment_method: "credit_card",
          credit_card: {
            operation_type: "auth_and_capture",
            installments: 1,
            statement_descriptor: "CDL SM",
            card: {
              number: data.billing.cardNumber,
              holder_name: data.billing.cardName,
              exp_month: +expirationParts[0],
              exp_year: +expirationParts[1],
              cvv: data.billing.cardCVC,
              billing_address: billingAddress
            }
          }
        }
      ];
    }

    Service.createMaterialOrderGateway(checkoutData)
      .then(responseData => {
        registerTransaction(contextData, responseData, materialOrderId);
      })
      .catch(err => {
        Service.registerTransactionError({
          pagarme_response: err,
          material_order_id: materialOrderId
        });
        ErrorHelper.notifyError(err);
        formik.setSubmitting(false);
      });
  };

  const registerTransaction = (
    contextData: any,
    pagarme: any,
    materialOrderId: number
  ) => {
    const data = {
      transaction_token: pagarme.token,
      transaction_id: pagarme.transaction_id,
      boleto_url: pagarme.boleto_url,
      boleto_barcode: pagarme.boleto_barcode,
      acquirer_id: pagarme.acquirer_id,
      date_created: pagarme.date_created,
      material_order_id: materialOrderId,
      payment_method: contextData.billing.formPayment
    };

    Service.registerTransaction(data)
      .then(() => {
        clearMaterialSolicitationContextData();
        navigate("/material/new/success", {
          state: {
            paymentMethod: contextData.billing.formPayment,
            boletoUrl: pagarme?.boleto_url
          },
          replace: true
        });
      })
      .catch(err => {
        ErrorHelper.notifyError(err);
        formik.setSubmitting(false);
      });
  };

  const getAnalyses = (analyses: any) => {
    const arr: { analysis_type: string }[] = [];
    Object.keys(analyses)
      .filter(k => analyses[k])
      .map(item => arr.push({ analysis_type: String(item).toUpperCase() }));
    return arr;
  };

  const formik: FormikProps<PaymentProps> = useFormik<PaymentProps>({
    initialValues,
    validationSchema: PaymentSchema,
    onSubmit: data => {
      contextData.billing = data.billing;
      if (contextData.billing.city && contextData.billing.state) {
        Service.verifyCity(contextData.billing.city, contextData.billing.state)
          .then(() => {
            sendForm(data);
          })
          .catch(err => {
            ErrorHelper.notifyError(err);
            formik.setSubmitting(false);
          });
      } else {
        sendForm(data);
      }
    }
  });

  useEffect(() => {
    let notifyMessage = "";
    const labels: string[] = [];

    if (formik?.errors?.billing) {
      (
        Object.keys(formik.errors.billing) as (keyof FieldStrings["payment"])[]
      ).forEach(key => {
        if (Locale?.fields?.payment?.[key]) {
          labels.push(Locale.fields.payment[key]);
        }
      });

      notifyMessage = labels.join(", ");
      if (notifyMessage) {
        notify(`Existe um erro nos seguintes campos: ${notifyMessage}`);
      }
    }
  }, [formik.submitCount]);

  useEffect(() => {
    const previousPagesAreEmpty =
      isEqual(contextData.analysis, initialContextValues.analysis) ||
      isEqual(contextData.delivery, initialContextValues.delivery);

    if (previousPagesAreEmpty) {
      navigate("/");
    }
  });

  return (
    <NewMaterialContext.Provider value={contextData}>
      <Template formik={formik} cart notification className="margin-y">
        <form onSubmit={formik.handleSubmit}>
          <HeaderPage
            title={Locale.pages.newMaterial.payment.title}
            description={Locale.pages.newMaterial.payment.description}
            goBack={() => {
              contextData.billing = formik?.values?.billing;
              navigate("/material/new/address");
            }}
          />
          <fieldset disabled={formik.isSubmitting}>
            <Billing formik={formik} autofill />
            <Address formik={formik} />
            <FinancialResponsible formik={formik} />
            <FormPayment formik={formik} />
          </fieldset>
          <Summary formik={formik}>
            <Button
              type="submit"
              title={Locale.actions.payAndFinish}
              fullWidth
              isLoading={formik.isSubmitting}
            />
          </Summary>
        </form>
      </Template>
    </NewMaterialContext.Provider>
  );
};

export default Payment;
